import React, { useMemo, useState, useEffect } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import { SpotlightPulse, SpotlightTarget } from '@atlaskit/onboarding';
import { Box, xcss, Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	FireScreenAnalytics,
	SCREEN,
	ContextualAnalyticsData,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { toProformaEnvironment } from '@atlassian/jira-proforma-utils/src/common/utils/to-proforma-environment/index.tsx';
import { getUrls, ANALYTICS_CHANNEL } from '@atlassian/jira-proforma-utils/src/index.tsx';
import { useSeenStatus } from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/controllers/seen-status/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { createUtils2 } from '@atlassian/proforma-common-core/jira-common-utils';
import {
	type Form,
	AddFormStoreContainer,
	AddForm,
	type Forms,
} from '@atlassian/proforma-form-system/add-form';
import {
	useTemplateLibraryActions,
	TemplateLibraryContainer,
} from '@atlassian/proforma-template-library-store';
import { AnalyticsProvider } from '@atlassian/proforma-utils/analytics';
import { fg } from '@atlassian/jira-feature-gating';
import { FormRestrictionStatus } from '@atlassian/proforma-form-security';
import { packageName, teamName } from '../../common/constants.tsx';
import { useFetchProformaProjectForms } from '../../services/proforma-project-forms/index.tsx';
import { SPOTLIGHT_TARGET_ID } from './changeboarding-spotlight/constants.tsx';
import { ChangeboardingSpotlight } from './changeboarding-spotlight/main.tsx';
import messages from './messages.tsx';
import { ProformaFormTemplateLibrary } from './proforma-form-template-library/index.tsx';
import type { CreatedForm } from './proforma-form-template-library/types.tsx';
import type { FormEmptyStateProps } from './types.tsx';

const PROFORMA_RTC_EMPTY_STATE_CHANGEBOARDING_USER_PROPERTY_KEY =
	'proforma-rtc-empty-state-changeboarding-seen-status';

const FormSelector = ({
	onCancel,
	onAdd,
}: {
	onCancel: () => void;
	onAdd: (form: Form) => void;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fetchProformaProjectForms = useFetchProformaProjectForms();
	const { locale } = useIntl();
	const environment = useEnvironment();
	const cloudId = useCloudId();

	const { errorUtils } = useMemo(() => {
		const proformaEnvironment = toProformaEnvironment(environment);
		const { api: apiUrl, jira: jiraUrl } = getUrls(proformaEnvironment, cloudId);
		return createUtils2(
			jiraUrl,
			apiUrl,
			proformaEnvironment,
			'RtcFormEmptyState',
			() => () => undefined,
			createAnalyticsEvent,
			locale,
		);
	}, [cloudId, createAnalyticsEvent, environment, locale]);

	const loadFormsApi = useMemo(
		() => ({
			loadForms: async (): Promise<Forms> => {
				const projectForms = await fetchProformaProjectForms();
				return {
					recommended: [],
					available: projectForms.map(({ id, name, restrictionStatus }) => ({
						label: name,
						value: id,
						restrictionStatus: restrictionStatus ?? FormRestrictionStatus.OPEN,
					})),
				};
			},
		}),
		[fetchProformaProjectForms],
	);

	return (
		<AddFormStoreContainer api={loadFormsApi} errorUtils={errorUtils}>
			<AddForm onCancel={onCancel} onAdd={onAdd} />
		</AddFormStoreContainer>
	);
};

export const FormsNotLoadedError = () => {
	const { formatMessage } = useIntl();
	return (
		<Margin data-testid="proforma-request-type-config.ui.form-empty-state.forms-not-loaded-error">
			<Card>
				<ContentColumn>
					<Text weight="bold">{formatMessage(messages.title)}</Text>
					<SectionMessage appearance="error">
						<Text>{formatMessage(messages.formsNotLoadedError)}</Text>
					</SectionMessage>
				</ContentColumn>
			</Card>
		</Margin>
	);
};

const FormEmptyStateInternal = ({
	onAdd,
	onPulseShown,
	onTemplateFormCreated,
	hasTemplateForms,
}: FormEmptyStateProps) => {
	const [{ isSeen: hasSeenSpotlight }, { markAsSeen: markSpotlightAsSeen }] = useSeenStatus({
		userPropertyKey: PROFORMA_RTC_EMPTY_STATE_CHANGEBOARDING_USER_PROPERTY_KEY,
		isSeenValue: true,
		packageName,
		teamName,
	});
	const [canShowPulse, setCanShowPulse] = useState(true);
	const shouldShowPulse = canShowPulse && !hasSeenSpotlight;
	const shouldShowSpotlight = !canShowPulse && !hasSeenSpotlight;

	const { formatMessage } = useIntl();

	const [isSelectorDisplayed, setIsSelectorDisplayed] = useState(false);
	const [isAttachFormDropdownOpen, setIsAttachFormDropdownOpen] = useState(false);
	const { setIsTemplateLibraryOpen } = useTemplateLibraryActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (shouldShowPulse) {
			onPulseShown();
		}
	}, [onPulseShown, shouldShowPulse]);

	const withSpotlight = (onAcknowledgeSpotlight: () => void) => () => {
		if (shouldShowPulse) {
			setCanShowPulse(false);
			return;
		}

		if (shouldShowSpotlight) {
			markSpotlightAsSeen();
		}

		onAcknowledgeSpotlight();
	};

	const onAddFormClick = withSpotlight(() => {
		setIsSelectorDisplayed(true);
	});

	const openAttachFormDropdown = withSpotlight(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdown',
		});
		fireUIAnalytics(analyticsEvent, 'attachFormDropdown');
		setIsAttachFormDropdownOpen(true);
	});

	const onAttachFormButtonClick = withSpotlight(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(analyticsEvent, 'attachFormButton');
		setIsTemplateLibraryOpen(true);
	});

	const onCreateFromTemplateDropdownItemClick = withSpotlight(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdownItem',
		});
		fireUIAnalytics(analyticsEvent, 'createFromTemplateDropdownItem');
		setIsTemplateLibraryOpen(true);
	});

	const onSelectFormDropdownItemClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdownItem',
		});
		fireUIAnalytics(analyticsEvent, 'selectExistingFormDropdownItem');
		setIsSelectorDisplayed(true);
	};

	const onCancelAddForm = () => {
		setIsSelectorDisplayed(false);
	};

	const onAttachFormDropDownOpenChange = (openChangeArgs: OnOpenChangeArgs) => {
		openChangeArgs.isOpen ? openAttachFormDropdown() : setIsAttachFormDropdownOpen(false);
	};

	const onAddSelectedForm = (form: Form) => {
		onAdd(form);
	};

	const onFormCreated = (createdForm: CreatedForm) => {
		setIsTemplateLibraryOpen(false);
		onTemplateFormCreated();
		onAdd({
			label: createdForm.formName,
			value: createdForm.formId,
		});
	};

	useEffect(() => () => markSpotlightAsSeen(), [markSpotlightAsSeen]);

	let attachFormButton;
	let onAcknowledgeSpotlight;
	let instructionMsg;
	if (expValEquals('rtc_form_empty_state_to_all_user', 'cohort', 'experiment')) {
		if (hasTemplateForms) {
			instructionMsg = formatMessage(messages.searchAndCreateFormDescription);
			onAcknowledgeSpotlight = openAttachFormDropdown;
			attachFormButton = (
				<DropdownMenu
					trigger={formatMessage(messages.attachFormButton)}
					isOpen={isAttachFormDropdownOpen}
					onOpenChange={onAttachFormDropDownOpenChange}
					shouldRenderToParent={fg('should-render-to-parent-should-be-true-jsm-request')}
				>
					<DropdownItemGroup>
						<DropdownItem onClick={onSelectFormDropdownItemClick}>
							{formatMessage(messages.selectExistingFormButton)}
						</DropdownItem>
						<DropdownItem onClick={onCreateFromTemplateDropdownItemClick}>
							{formatMessage(messages.createFromTemplateButton)}
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			);
		} else {
			instructionMsg = formatMessage(messages.createFormDescription);
			onAcknowledgeSpotlight = onAttachFormButtonClick;
			attachFormButton = (
				<Button onClick={onAttachFormButtonClick}>
					{formatMessage(messages.attachFormButton)}
				</Button>
			);
		}
	} else {
		instructionMsg = formatMessage(messages.subtitle);
		onAcknowledgeSpotlight = onAddFormClick;
		attachFormButton = (
			<Button onClick={onAddFormClick}>{formatMessage(messages.addFormButton)}</Button>
		);
	}

	return (
		<Margin>
			<ContextualAnalyticsData sourceName="proformaRtcFormEmptyState" sourceType={SCREEN}>
				<FireScreenAnalytics />
				<SpotlightTarget name={SPOTLIGHT_TARGET_ID}>
					<Card>
						{isSelectorDisplayed ? (
							<AnalyticsProvider channel={ANALYTICS_CHANNEL}>
								<ContentColumn>
									<Text weight="bold">{formatMessage(messages.title)}</Text>
									{expValEquals(
										'rtc_form_empty_state_to_all_user',
										'cohort',
										'experiment',
									) ? null : (
										<Text size="small">{formatMessage(messages.subtitle)}</Text>
									)}
								</ContentColumn>
								<Box xcss={formSelectorWrapperStyles}>
									<FormSelector onCancel={onCancelAddForm} onAdd={onAddSelectedForm} />
								</Box>
							</AnalyticsProvider>
						) : (
							<>
								<EmptyStateRow>
									<ContentColumn>
										<Text weight="bold">{formatMessage(messages.title)}</Text>
										<Text size="small">{instructionMsg}</Text>
									</ContentColumn>
									<SpotlightWrapper>
										<SpotlightPulse radius={3} pulse={shouldShowPulse}>
											{attachFormButton}
										</SpotlightPulse>
									</SpotlightWrapper>
								</EmptyStateRow>
								{expValEquals('rtc_form_empty_state_to_all_user', 'cohort', 'experiment') && (
									<ProformaFormTemplateLibrary onFormCreated={onFormCreated} />
								)}
							</>
						)}
					</Card>
					{shouldShowSpotlight && (
						<ChangeboardingSpotlight onAcknowledge={onAcknowledgeSpotlight} />
					)}
				</SpotlightTarget>
			</ContextualAnalyticsData>
		</Margin>
	);
};

export const FormEmptyState = (props: FormEmptyStateProps) =>
	expValEquals('rtc_form_empty_state_to_all_user', 'cohort', 'experiment') ? (
		<TemplateLibraryContainer>
			<FormEmptyStateInternal {...props} />
		</TemplateLibraryContainer>
	) : (
		<FormEmptyStateInternal {...props} />
	);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Margin = styled.div({
	width: '100%',
	marginTop: `${token('space.150')}`,
	marginBottom: `${token('space.050')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Card = styled.div({
	width: '100%',
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	borderRadius: '3px',
	boxSizing: 'border-box',
	boxShadow: `${token('elevation.shadow.raised')}, ${token('elevation.shadow.raised')}`,
	backgroundColor: token('color.background.input'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyStateRow = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentColumn = styled.div({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpotlightWrapper = styled.span({
	position: 'static',
});

const formSelectorWrapperStyles = xcss({
	marginTop: 'space.100',
});
